import { useRef } from 'react';
import FormField from './FormField';
import { BASE_CLASSES } from './TextField';
import { formatPhone, normalizePhone } from '../../lib/validation/customer.mjs';

/**
 * US phone input with (555) 555-5555 formatting. Replaces react-input-mask, which was used for
 * this one field, has been unmaintained for years, and uses legacy lifecycle patterns that
 * warn under React 18 StrictMode.
 *
 * CARET HANDLING -- the part that makes hand-rolled masks feel broken:
 *
 *  1. Reformatting on every change and writing the result back moves the caret to the END,
 *     because that is where browsers place it after a programmatic value set. Typing into the
 *     middle of a formatted number would throw the cursor away. We restore the caret by
 *     counting DIGITS before it rather than characters, since the formatting characters shift.
 *
 *  2. Backspacing over a formatting character -- the ")" in "(555) " -- deletes a character
 *     the formatter immediately regenerates, so the value never changes and the keypress
 *     appears to do nothing. We detect an unchanged digit count on a delete and remove one
 *     more digit, which is what the user expects.
 */
export default function PhoneField({ name, field, value, error, onChange, onBlur, inputRef }) {
  const localRef = useRef(null);
  const ref = inputRef || localRef;

  const handleChange = (e) => {
    const input = e.target;
    const raw = input.value;
    const caret = input.selectionStart ?? raw.length;

    const isDeleting =
      e.nativeEvent?.inputType === 'deleteContentBackward' ||
      e.nativeEvent?.inputType === 'deleteContentForward';

    let digits = normalizePhone(raw);

    // Case 2: a delete that removed only a formatting character. Drop the digit before the
    // caret so the keystroke has a visible effect.
    if (isDeleting && digits === normalizePhone(value)) {
      const digitsBeforeCaret = normalizePhone(raw.slice(0, caret)).length;
      digits =
        digits.slice(0, Math.max(0, digitsBeforeCaret - 1)) + digits.slice(digitsBeforeCaret);
    }

    const formatted = formatPhone(digits);
    onChange(name, formatted);

    // Case 1: restore the caret by digit position. Counting digits (not characters) keeps the
    // cursor logically in place as separators are inserted around it.
    const digitsBeforeCaret = normalizePhone(raw.slice(0, caret)).length;
    const target = Math.max(0, isDeleting ? digitsBeforeCaret - 1 : digitsBeforeCaret);

    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;

      let seen = 0;
      let pos = formatted.length;
      for (let i = 0; i < formatted.length; i += 1) {
        if (/\d/.test(formatted[i])) {
          seen += 1;
          if (seen === target) {
            pos = i + 1;
            break;
          }
        }
      }
      if (target === 0) pos = formatted.length ? 1 : 0;

      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <FormField id={name} label={field.label} required={field.required} error={error}>
      {({ id, describedBy, invalid }) => (
        <input
          ref={ref}
          id={id}
          name={name}
          type="tel"
          value={value}
          autoComplete="tel"
          inputMode="tel"
          placeholder="(555) 555-5555"
          maxLength={14} // "(555) 555-5555"
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          onChange={handleChange}
          onBlur={(e) => onBlur(name, e.target.value)}
          className={`${BASE_CLASSES} ${
            invalid ? 'border-danger-500 focus:border-danger-500' : 'border-gray-300 focus:border-primary-500'
          }`}
        />
      )}
    </FormField>
  );
}
